/**
 * JS Placement Tower - Creates two planes from JS Placement images
 * 
 * SIMPLIFIED: Only two layers (layer1 and layer2) with explicit transforms
 * Both layers use the same shared material (computed once)
 * Uses pre-compiled materials with texture caching for better performance
 */

import type * as THREE_NS from 'three';
import { TOWER_CONFIG, type LayerConfig, type MaterialConfig } from './jsPlacementTowerConfig.js';

// Texture cache for pre-compiled materials (avoids runtime loading)
const textureCache = new Map<string, THREE_NS.Texture>();

/**
 * Load texture from URL asynchronously (off main thread, with size limiting)
 */
async function loadTexture(THREE: typeof THREE_NS, url: string): Promise<THREE_NS.Texture> {
  const { TextureLoader } = THREE as any;
  const loader = new TextureLoader();

  return new Promise((resolve, reject) => {
    loader.load(
      url,
      (texture: THREE_NS.Texture) => {
        texture.wrapS = (THREE as any).RepeatWrapping;
        texture.wrapT = (THREE as any).RepeatWrapping;
        // Set texture repeat to 4x4 for tiling
        texture.repeat.set(1, 1);
        // Limit texture size to 512x512 for performance
        if (texture.image) {
          const maxSize = 128;
          if (texture.image.width > maxSize || texture.image.height > maxSize) {
            // Create a smaller canvas version
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            if (ctx) {
              let width = texture.image.width;
              let height = texture.image.height;

              if (width > maxSize || height > maxSize) {
                if (width > height) {
                  height = Math.round((height / width) * maxSize);
                  width = maxSize;
                } else {
                  width = Math.round((width / height) * maxSize);
                  height = maxSize;
                }
              }

              canvas.width = width;
              canvas.height = height;
              ctx.drawImage(texture.image, 0, 0, width, height);

              // Create new texture from resized canvas
              const resizedTexture = new (THREE as any).CanvasTexture(canvas);
              resizedTexture.wrapS = (THREE as any).RepeatWrapping;
              resizedTexture.wrapT = (THREE as any).RepeatWrapping;
              // Set texture repeat to 4x4 for tiling
              resizedTexture.repeat.set(4, 4);
              resolve(resizedTexture);
              return;
            }
          }
        }
        resolve(texture);
      },
      undefined,
      reject
    );
  });
}

/**
 * Parse hex color string to number
 */
function parseHexColor(hex: string): number {
  // Remove '0x' prefix if present
  const cleanHex = hex.startsWith('0x') ? hex.slice(2) : hex;
  return parseInt(cleanHex, 16);
}

/**
 * Create material synchronously using cached textures (pre-compiled)
 * Both layers use the same material
 */
function createSharedMaterial(
  THREE: typeof THREE_NS,
  materialConfig: MaterialConfig,
  layer: LayerConfig
): THREE_NS.MeshStandardMaterial | null {
  const { MeshStandardMaterial } = THREE as any;

  const material = new MeshStandardMaterial({
    side: (THREE as any).DoubleSide,
    transparent: true,
    opacity: materialConfig.opacity!,
    alphaTest: materialConfig.alphaTest!,
    depthWrite: false,
    color: new (THREE as any).Color(parseHexColor(materialConfig.color!)),
    roughness: materialConfig.roughness!,
    metalness: materialConfig.metalness!,
    emissive: new (THREE as any).Color(parseHexColor(materialConfig.emissive!)),
    emissiveIntensity: materialConfig.emissiveIntensity!,
  });

  // Use cached textures (pre-loaded)
  const diffuseUrl = layer.seamless || layer.raster;
  const normalUrl = layer.seamlessNormal || layer.normal;
  const colorUrl = layer.seamlessColor || layer.color;

  if (diffuseUrl) {
    const diffuseTexture = textureCache.get(diffuseUrl);
    if (diffuseTexture) {
      diffuseTexture.format = (THREE as any).RGBAFormat;
      material.map = diffuseTexture;
      material.alphaMap = diffuseTexture;
    }
  }

  if (normalUrl) {
    const normalTexture = textureCache.get(normalUrl);
    if (normalTexture) {
      material.normalMap = normalTexture;
      const normalScale = materialConfig.normalScale!;
      material.normalScale = new (THREE as any).Vector2(normalScale, normalScale);
    }
  }

  if (colorUrl) {
    const colorTexture = textureCache.get(colorUrl);
    if (colorTexture) {
      colorTexture.format = (THREE as any).RGBAFormat;
      material.emissiveMap = colorTexture;
      material.emissiveIntensity = materialConfig.emissiveIntensity! * 2.5;
    }
  }

  return material;
}

/**
 * Pre-load textures into cache (called once at startup)
 */
async function preloadTextures(THREE: typeof THREE_NS, layer1: LayerConfig, layer2: LayerConfig): Promise<void> {
  const basePath = '/jsplacement';
  const processedPath = `${basePath}/processed`;

  const textureUrls = new Set<string>();

  // Collect all unique texture URLs from both layers
  [layer1, layer2].forEach(layer => {
    if (layer.seamless) textureUrls.add(layer.seamless);
    if (layer.raster) textureUrls.add(layer.raster);
    if (layer.seamlessNormal) textureUrls.add(layer.seamlessNormal);
    if (layer.normal) textureUrls.add(layer.normal);
    if (layer.seamlessColor) textureUrls.add(layer.seamlessColor);
    if (layer.color) textureUrls.add(layer.color);
  });

  // Load all textures in parallel
  const loadPromises = Array.from(textureUrls).map(async (url) => {
    if (textureCache.has(url)) return; // Already cached

    try {
      // Try processed version first
      const processedUrl = `${processedPath}/processed_${url}`;
      try {
        const texture = await loadTexture(THREE, processedUrl);
        textureCache.set(url, texture);
        return;
      } catch {
        // Fall back to original
      }

      // Load original version
      const texture = await loadTexture(THREE, `${basePath}/${url}`);
      textureCache.set(url, texture);
    } catch (error) {
    }
  });

  await Promise.all(loadPromises);
}

/**
 * Create JS Placement tower - SIMPLIFIED: Only two layers with explicit transforms
 * Both layers use the same material (computed once)
 */
export async function createJSPlacementTower(
  scene: THREE_NS.Scene,
  THREE: typeof THREE_NS
): Promise<THREE_NS.Group> {
  const { Group, PlaneGeometry, Mesh } = THREE as any;

  const config = TOWER_CONFIG;

  // Pre-load textures for both layers
  await preloadTextures(THREE, config.layer1, config.layer2);

  // Create shared material (both layers use the same material)
  // Use layer1's textures for the shared material
  const sharedMaterial = createSharedMaterial(THREE, config.material, config.layer1);

  if (!sharedMaterial) {
    return new Group();
  }

  // Create tower group
  const towerGroup = new Group();
  towerGroup.position.set(0, 0, 0);

  // Plane size (square) - base size 10, scaled by config
  const basePlaneSize = 10;
  const planeSize = basePlaneSize * config.scale;

  // Create geometry once (both layers use same size)
  const geometry = new PlaneGeometry(planeSize, planeSize);

  // Render layer 1 with explicit position and rotation
  const mesh1 = new Mesh(geometry, sharedMaterial);
  mesh1.position.set(config.layer1.position.x, config.layer1.position.y, config.layer1.position.z);
  if (config.layer1.rotation) {
    const rotX = (config.layer1.rotation.x ?? 0) * (Math.PI / 180);
    const rotY = (config.layer1.rotation.y ?? 0) * (Math.PI / 180);
    const rotZ = (config.layer1.rotation.z ?? 0) * (Math.PI / 180);
    mesh1.rotation.set(rotX, rotY, rotZ);
  }
  towerGroup.add(mesh1);

  // Render layer 2 with explicit position and rotation
  const mesh2 = new Mesh(geometry, sharedMaterial);
  mesh2.position.set(config.layer2.position.x, config.layer2.position.y, config.layer2.position.z);
  if (config.layer2.rotation) {
    const rotX = (config.layer2.rotation.x ?? 0) * (Math.PI / 180);
    const rotY = (config.layer2.rotation.y ?? 0) * (Math.PI / 180);
    const rotZ = (config.layer2.rotation.z ?? 0) * (Math.PI / 180);
    mesh2.rotation.set(rotX, rotY, rotZ);
  }
  towerGroup.add(mesh2);

  // Add tower to scene
  scene.add(towerGroup);

  return towerGroup;
}

